import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray, Controller, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { BaseEditModal, CircularPlusButton, Input } from 'components';
import { parse } from 'papaparse';
import { TickIconCircle } from 'assets';
import styles from './styles.module.scss';

const schema = yup.object().shape({
  candidates: yup
    .array()
    .of(
      yup.object().shape({
        full_name: yup.string().required('Name is required'),
        email: yup.string().required('Email is required').email('Invalid email format'),
      })
    )
    .min(1, 'At least one candidate is required')
    .required(), // Ensure candidates is not undefined
});

interface Candidate {
  full_name: string;
  email: string;
}

interface FormData {
  candidates: Candidate[];
}

interface InviteCandidatesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (payload: { candidates?: Candidate[]; file?: File }) => void;
  isLoading: boolean;
  assessmentId: string | null;
  initialCandidates?: Candidate[];
}

export const InviteCandidatesModal: React.FC<InviteCandidatesModalProps> = ({
  isOpen,
  onClose,
  onSave,
  isLoading,
  assessmentId,
  initialCandidates,
}) => {
  const [isImportView, setIsImportView] = useState(false);

  const { register, control, handleSubmit, reset, formState: { errors } } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      candidates: initialCandidates && initialCandidates.length > 0
        ? initialCandidates
        : [{ full_name: '', email: '' }],
    },
  });

  // Reset form when initialCandidates change or modal opens with new candidates
  useEffect(() => {
    if (isOpen && initialCandidates && initialCandidates.length > 0) {
      reset({ candidates: initialCandidates });
    }
  }, [isOpen, initialCandidates, reset]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'candidates',
  });

  const onSubmit: SubmitHandler<FormData> = (data) => {
    // Since schema enforces candidates is an array with at least one element, it won't be undefined
    onSave({ candidates: data.candidates });
    onClose();
    reset();
  };

  const handleAddCandidate = () => {
    append({ full_name: '', email: '' });
  };

  const handleImportCSV = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onSave({ file });
      // Parse CSV to update form state (optional, for display purposes)
      parse(file, {
        header: true,
        complete: (result) => {
          const csvData = result.data
            .filter((row: any) => row.full_name && row.email)
            .map((row: any) => ({
              full_name: row.full_name.trim(),
              email: row.email.trim(),
            }));
          if (csvData.length > 0) {
            reset({ candidates: csvData });
          }
        },
        error: (error) => {
          alert('Error parsing CSV file: ' + error.message);
        },
      });
    }
  };

  const handleDownloadSampleCSV = () => {
    const csvContent = `Fullname,Email\nJohn Doe,john.doe@example.com\nJane Smith,jane.smith@example.com\nMichael Brown,michael.brown@example.com\nEmily Davis,emily.davis@example.com`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'sample_candidates.csv';
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const switchToImportView = () => {
    setIsImportView(!isImportView);
  };

  if (!isOpen) return null;

  return (
    <BaseEditModal
      isOpen={isOpen}
      onClose={onClose}
      onSwitch={switchToImportView}
      cvType={true}
      title="Invite Candidates"
      onSubmit={handleSubmit(onSubmit)}
      cancelButtonText={isImportView ? 'Back to Manual Entry' : 'Import CSV'}
      saveButtonText="Confirm"
      footerClassName={styles.buttonGroup}
      cancelButtonClassName={styles.cancelButton}
      saveButtonClassName={styles.saveButton}
    >
      {isImportView ? (
        <div className={styles.importView}>
          <h3 className={styles.importTitle}>Import Candidates as CSV</h3>

          <div className={styles.instructionRow}>
            <TickIconCircle />
            <div className={styles.instructionText}>
              <span>Use supported headings: candidates' names, candidates' email</span>
              <br />
              <button
                type="button"
                className={styles.sampleLink}
                onClick={handleDownloadSampleCSV}
              >
                (Download a sample CSV)
              </button>
            </div>
          </div>

          <div className={styles.dropZone}>
            <div className={styles.uploadIcon}>
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <path d="M13.3333 26.6667L20 20L26.6667 26.6667" stroke="#667085" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M20 20V35" stroke="#667085" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M33.9833 30.9833C36.0533 29.8083 37.5 27.6917 37.5 25.1667C37.5 21.4833 34.5167 18.5 30.8333 18.5C30.0833 18.5 29.3833 18.6583 28.75 18.9333C27.2667 12.7917 21.8 8.33334 15.2333 8.33334C7.73333 8.33334 1.66667 14.4 1.66667 21.9C1.66667 25.6333 3.41667 28.95 6.15 31.1" stroke="#667085" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <p className={styles.dropText}>Select a CSV file to import</p>
            <p className={styles.dropSubtext}>or drag and drop it here</p>
            <input
              type="file"
              accept=".csv"
              onChange={handleImportCSV}
              className={styles.fileInput}
              disabled={isLoading}
            />
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className={styles.formSection}>
          {fields.map((field, index) => (
            <div key={field.id} className={styles.candidateRow}>
              <div className={styles.inputGroup}>
                <Controller
                  name={`candidates.${index}.full_name`}
                  control={control}
                  render={({ field }) => (
                    <Input
                      label={`${fields.length > 1 ? `${index + 1}. ` : ''}Name`}
                      placeholder="Candidate's name"
                      name={`candidates.${index}.full_name`}
                      register={register}
                      validatorMessage={errors.candidates?.[index]?.full_name?.message}
                      required
                      disabled={isLoading}
                      parentClassName={styles.inputWrapper}
                    />
                  )}
                />
              </div>
              <div className={styles.inputGroup}>
                <Controller
                  name={`candidates.${index}.email`}
                  control={control}
                  render={({ field }) => (
                    <Input
                      label="Email"
                      placeholder="Candidate's email"
                      name={`candidates.${index}.email`}
                      register={register}
                      validatorMessage={errors.candidates?.[index]?.email?.message}
                      required
                      Icon={() => <span className={styles.emailIcon}>✉</span>}
                      iconClassName={styles.emailIcon}
                      disabled={isLoading}
                      parentClassName={styles.inputWrapper}
                    />
                  )}
                />
              </div>
              {index > 0 && (
                <button
                  type="button"
                  onClick={() => remove(index)}
                  className={styles.removeButton}
                  disabled={isLoading}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={handleAddCandidate}
            className={styles.addButton}
            disabled={isLoading}
          >
            <CircularPlusButton onClick={() => { }} className={styles.plusButton} /> Add more candidates
          </button>
        </form>
      )}
    </BaseEditModal>
  );
};